
import App from '@/App.vue'
import vuetify from '@/plugins/vuetify'
import { loadFonts } from '@/plugins/webfontloader'
import router from '@/router'
import type { Router } from 'vue-router'
import { useAppStore } from '@/stores/app'
import { createPinia } from 'pinia'
import { VueQueryPlugin } from '@tanstack/vue-query'
import { createApp, markRaw } from 'vue'
import i18n from '@/plugins/i18n'
import VueDOMPurifyHTML from 'vue-dompurify-html'
import '@sass/app.scss'
import * as Sentry from '@sentry/vue'
import yupSetup from '@/plugins/yupSetup'
import { initializeGTM } from '@/utils/gtm'

loadFonts()

declare module 'pinia' {
  export interface PiniaCustomProperties {
    // type the router added by the plugin above (#adding-new-external-properties)
    router: Router
  }
}

// Handle Vite preload errors
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault()

  // Force to reload the page and refresh old chunks after Amplify build
  window.location.href = window.location.href.split('?')[0] + '?_t=' + Date.now()
})

// Create vue app
const app = createApp(App)
const pinia = createPinia()

// Globally load router into Pinia
pinia.use(({ store }) => {
  store.router = markRaw(router)
})

// Use plugins
app.use(vuetify)
app.use(createPinia())
app.use(router)
app.use(VueQueryPlugin, {
  enableDevtoolsV6Plugin: true,
})
app.use(i18n)
app.use(VueDOMPurifyHTML, {
  hooks: {
    afterSanitizeAttributes: (currentNode: HTMLElement) => {
      // set all elements owning target to target=_blank
      if ('target' in currentNode) {
        currentNode.setAttribute('target', '_blank')
        currentNode.setAttribute('rel', 'noopener')
      }
    },
  },
})
// Pull in the App Config passed from PHP

const appStore = useAppStore()

appStore.setConfigs()
appStore.$i18n = i18n

if (appStore.config.GTM_ID) {
  initializeGTM({ id: appStore.config.GTM_ID })
}

const sentryIntegrations = [
  Sentry.browserTracingIntegration({ router }),
  Sentry.captureConsoleIntegration({
    levels: ['error'] // Ignore info and debug logs
  }),
]

if (import.meta.env.VITE_SENTRY_USER_FEEDBACK) {
  sentryIntegrations.push(Sentry.feedbackIntegration({ colorScheme: 'system', autoInject: false }))
}

Sentry.init({
  app,
  debug: false,
  dsn: import.meta.env.VITE_SENTRY_DSN_PUBLIC,
  release: 'MyFJv2@' + import.meta.env.VITE_APP_VERSION || 'unknown',
  environment: import.meta.env.VITE_APP_ENV,
  trackComponents: true,
  integrations: sentryIntegrations,

  ignoreErrors: [
    /TSS:/i,
    /MBTSS/i,
    /injection-tss/i,
    /Couldn't resolve component/
  ],

  denyUrls: [
    /extensions\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-extension:\/\//i,
  ],

  beforeSend(event, hint) {
    if (!event.exception || hint?.originalException === undefined) {
      return null
    }

    if (import.meta.env.VITE_SENTRY_USER_FEEDBACK && event.event_id) {
      Sentry.showReportDialog({ eventId: event.event_id })
    }

    return event
  },
})

yupSetup()

// Mount vue app
app.mount('#app')
